import { inject, Injectable } from "@angular/core";
import { LogEntry, LogLevel } from "./log-entry.model";
import { LogAggregatorClient } from "./log-aggregator-client.service";
import { ConfigService } from "../config/config.service";
import { APP_VERSION } from "../config/app-version";
import { nativeConsole } from "./native-console";
import { toJsonValue } from "./log-value-sanitizer";

const LEVEL_ORDER: Record<LogLevel, number> = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };
const DEFAULT_LEVEL: LogLevel = "INFO";

function isLogLevel(value: string): value is LogLevel {
  return value in LEVEL_ORDER;
}

function generateSessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The only logging API application code should call — `logger.debug/info/warn/error(tag, message, context?)`.
 *
 * Filters out entries below the configured minimum level (`ConfigService.logLevel`), mirrors to
 * real `console.*` only when `logLevel` is configured as `"DEBUG"` (so production builds stay
 * silent on the console while still persisting every entry), and forwards every entry that
 * passes the level filter to `LogAggregatorClient` — never touching IndexedDB directly itself.
 *
 * `context` accepts any value (a plain object, an `Error`/`DOMException`, an HTTP response, a
 * circular structure...). It's sanitized once, here, via `toJsonValue()` — and only for
 * entries that pass the level filter — so no caller ever needs its own sanitization.
 *
 * `console-interceptor.ts`, `app-error-handler.ts`, and `cc7-logger-bridge.ts` all route through
 * this same service, so every capture path shares one level filter (with no bypass), one
 * sanitization step, and one persistence pipeline. The console interceptor also calls
 * `isLevelEnabled()` itself, so a below-level raw `console.*` call isn't printed either.
 *
 * @see Docs/app-logs.md
 */
@Injectable({ providedIn: "root" })
export class Logger {
  private readonly configService = inject(ConfigService);
  private readonly aggregator = inject(LogAggregatorClient);
  private readonly sessionId = generateSessionId();

  debug(tag: string, message: string, context?: unknown): void {
    this.log("DEBUG", tag, message, context);
  }

  info(tag: string, message: string, context?: unknown): void {
    this.log("INFO", tag, message, context);
  }

  warn(tag: string, message: string, context?: unknown): void {
    this.log("WARN", tag, message, context);
  }

  /** `stack` is a separate parameter (not folded into `context`) so it lands in `LogEntry.stack`. */
  error(tag: string, message: string, context?: unknown, stack?: string): void {
    this.log("ERROR", tag, message, context, stack);
  }

  /** True when `level` is at or above the configured minimum `logLevel`. */
  isLevelEnabled(level: LogLevel): boolean {
    return LEVEL_ORDER[level] >= LEVEL_ORDER[this.minLevel];
  }

  private get minLevel(): LogLevel {
    const configuredValue = this.configService.logLevel;
    const configuredLogLevel = typeof configuredValue === "string" ? configuredValue.toUpperCase() : DEFAULT_LEVEL;
    return isLogLevel(configuredLogLevel) ? configuredLogLevel : DEFAULT_LEVEL;
  }

  private log(level: LogLevel, tag: string, message: string, context: unknown, stack?: string): void {
    if (!this.isLevelEnabled(level)) {
      return;
    }

    if (this.minLevel === "DEBUG") {
      // The raw value, so DevTools can still inspect it interactively.
      this.mirrorToConsole(level, tag, message, context);
    }

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      tag,
      message,
      context: context === undefined ? undefined : toJsonValue(context),
      stack,
      sessionId: this.sessionId,
      appVersion: APP_VERSION,
    };
    this.aggregator.enqueue(entry);
  }

  private mirrorToConsole(level: LogLevel, tag: string, message: string, context: unknown): void {
    // Always calls the native method (see `native-console.ts`), never `console[method]` itself —
    // otherwise this would re-enter the (possibly patched) console interceptor and recurse.
    const consoleMethod = level === "DEBUG" ? "log" : level === "INFO" ? "info" : level === "WARN" ? "warn" : "error";
    nativeConsole[consoleMethod](`[${tag}] ${message}`, context ?? "");
  }
}
